import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Map, List } from 'immutable'
import { getAuthIsLoggedIn } from 'services/auth'
import Button, { TYPES } from 'components/Button'
import IconV2, { ICON_TYPES } from 'components/Icon.v2'
import { FILTER_SET_DEFAULT } from 'services/filter-set'
import FiltersV2Dropdown from './Filters.v2Dropdown'
import {
  changeLocation,
  changeLocationQuery,
  getAppliedFiltersCount,
  getFilterSelectedOption,
} from './utils'

/**
 * @param {Object} props - The props
 * @param {String} props.filterSetType - The type of filter set object
 * @param {Map} props.filterSet - The object containing all loaded filter sets
 * @param {Map} props.staticText - The static text data
 * @param {Map} props.auth - The auth data
 * @param {Function} props.getFilterSetData - The get filter set action
 * @param {String} props.language - The user's language
 * @param {Object} props.location - The location object
 * @param {Object} props.history - The history object
 * @param {Function} props.setDefaultGa4Event - The GA4 event action
 * @param {List} props.defaultOptions - The list of options selected by default
 * @returns {JSX.Element} The Filters Set component
 */

function FiltersV2Set ({
  filterSetType,
  filterSet = Map(),
  staticText,
  getFilterSetData,
  language,
  location,
  history,
  setDefaultGa4Event,
  auth,
  defaultOptions,
}) {
  const containerRef = useRef(null)
  const [isExpanded, setIsExpanded] = useState(false)

  const appliedCount = getAppliedFiltersCount(location, filterSetType)

  const isLoggedIn = getAuthIsLoggedIn(auth)
  const isProcessing = filterSet.getIn([filterSetType, 'processing'])
  const currentFilterSet = filterSet.get(filterSetType)
  const filtersData = filterSet.getIn([filterSetType, 'data'], List())

  const filtersList = useMemo(() => {
    /* Some filter sets has filters without type
      so need to filter those out
    */
    return filtersData.filter((data) => !!data.get('type'))
  }, [filtersData])

  const containerClassName = [
    'filters-v2__set-container',
    isExpanded ? 'filters-v2__set-container--expanded' : null,
  ].filter((cls) => !!cls).join(' ')

  const applyFilter = useCallback((filterOption, param, changeType) => {
    // only query params can be changed for sort by filter set
    if (filterSetType === FILTER_SET_DEFAULT) {
      changeLocationQuery({
        filterOption,
        param,
        filterSetType,
        language,
        location,
        history,
        auth,
        isLoggedIn,
      })
    } else {
      changeLocation({
        filterOption,
        param,
        changeType,
        filtersList,
        filterSetType,
        language,
        location,
        history,
        auth,
        isLoggedIn,
      })
    }
    setIsExpanded(false)
  }, [
    language,
    location,
    history,
    auth,
    isLoggedIn,
  ])

  const handleClickOutside = useCallback((event) => {
    if (containerRef && containerRef.current && !containerRef.current.contains(event.target)) {
      setIsExpanded(false)
    }
  }, [containerRef])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside])

  useEffect(() => {
    if (!currentFilterSet && !isProcessing) {
      getFilterSetData({ filterSet: filterSetType })
    }
  }, [filterSetType, isProcessing, currentFilterSet])

  if (!filtersList || filtersList.size === 0) {
    return null
  }

  // If the filter set has more than 1 filter
  // component renders label 'Filters' with number of filters applied
  // else it renders single filter label with applied option name
  const isMultiFilterSet = filtersList.size > 1
  const defaultFilterOptions = filtersList.getIn([0, 'filter', 'options'])
  const defaultFilterParam = filtersList.getIn([0, 'param'])
  const defaultFilterValue = defaultOptions.get(defaultFilterParam, null)
  const selectedOption = isMultiFilterSet
    ? null
    : getFilterSelectedOption({
      options: defaultFilterOptions,
      location,
      param: defaultFilterParam,
      defaultValue: defaultFilterValue,
    })

  const label = isMultiFilterSet
    ? staticText.get('filters')
    : staticText.getIn(['filterSetLabels', filtersList.getIn([0, 'type']), 'label'])

  return (
    <div className="filters-v2__set" ref={containerRef}>
      <Button
        type={TYPES.DEFAULT}
        onClick={() => setIsExpanded(!isExpanded)}
        className="filters-v2__set-mobile-button"
      >
        {isMultiFilterSet
          ? <>
            {appliedCount > 0
              ? <span className="filters-v2__set-mobile-labels">
                <span>{staticText.get('applied')}</span>
                <span>
                  {`${appliedCount} ${appliedCount > 1 ? staticText.get('filters') : staticText.get('filter')}`}
                </span>
              </span>
              : <span className="filters-v2__set-mobile-label">
                {label}
              </span>
            }
          </>
          : <>
            {selectedOption
              ? <span className="filters-v2__set-mobile-labels">
                <span>{label}</span>
                <span>
                  {selectedOption.get('name')}
                </span>
              </span>
              : <span className="filters-v2__set-mobile-label">
                {label}
              </span>
            }
          </>
        }
        <IconV2
          className="filters-v2__set-mobile-icon"
          type={isMultiFilterSet ? ICON_TYPES.FILTER_VERTICAL : ICON_TYPES.CHEVRON_DOWN}
        />
      </Button>
      <div className={containerClassName}>
        <div className="filters-v2__set-options">
          <Button
            type={TYPES.GHOST}
            onClick={() => setIsExpanded(false)}
            className="filters-v2__set-mobile-back"
          >
            <IconV2
              className="filters-v2__set-mobile-back-icon"
              type={ICON_TYPES.CHEVRON_LEFT}
            />
            {label}
          </Button>
          {filtersList.map((filterData) => (
            <FiltersV2Dropdown
              key={filterData.get('type')}
              language={language}
              location={location}
              staticText={staticText}
              filterData={filterData}
              onOptionSelected={applyFilter}
              setDefaultGa4Event={setDefaultGa4Event}
              isOpenedByDefault={!isMultiFilterSet}
              defaultValue={defaultOptions.get(filterData.get('param'), null)}
            />
          ))}
        </div>
        <div
          className="filters-v2__set-mobile-backdrop"
          onClick={() => setIsExpanded(false)}
        />
      </div>
    </div>
  )
}

export default FiltersV2Set
